import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaSpinner, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { API_URL } from "../config/apiConfig";
import axios from "axios";

const FAQs = () => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/content/public/faqs`);
        if (response.data.success) {
          setFaqs(response.data.data || []);
        } else {
          setError("Failed to load FAQs");
        }
      } catch (err) {
        console.error("Error fetching FAQs:", err);
        setError("Unable to load FAQs. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  const categories = ["all", ...new Set(faqs.map(faq => faq.category))];
  const filteredFAQs = selectedCategory === "all"
    ? faqs
    : faqs.filter(faq => faq.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-primary text-4xl mx-auto mb-4" />
          <p className="text-gray-600">Loading FAQs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 pb-12">
      <div className="w-full max-w-[1180px] mx-auto px-4 md:px-6">
        {/* Back Button */}
        <div className="mb-3 md:mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors font-medium group"
          >
            <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Go Back
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-5 md:p-8 mb-4 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-gray-600 text-base md:text-lg">
            Find answers to common questions about CabZigo services
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-8">
            <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">Filter by Category</h3>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full transition-colors capitalize ${selectedCategory === category
                      ? "bg-primary text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* FAQs List */}
        <div className="space-y-3 md:space-y-4">
          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <p className="text-red-700">{error}</p>
            </div>
          ) : filteredFAQs.length > 0 ? (
            filteredFAQs.map((faq, index) => (
              <div
                key={faq._id || index}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                  className="w-full p-4 md:p-6 text-left hover:bg-gray-50 transition-colors flex justify-between items-center"
                >
                  <div className="flex-1">
                    <h3 className="text-base md:text-lg font-semibold text-gray-800">
                      {faq.question}
                    </h3>
                    {faq.category && (
                      <span className="text-xs text-gray-500 mt-1 inline-block capitalize">
                        {faq.category}
                      </span>
                    )}
                  </div>
                  <div className="ml-4">
                    {expandedIndex === index ? (
                      <FaChevronUp className="text-primary" />
                    ) : (
                      <FaChevronDown className="text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {expandedIndex === index && (
                  <div className="border-t border-gray-200 p-4 md:p-6 bg-gray-50">
                    <div
                      className="text-gray-700 leading-relaxed prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                    />
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-600">No FAQs found for this category.</p>
            </div>
          )}
        </div>

        {/* Related Links */}
        <div className="mt-6 pt-4 md:mt-12 md:pt-8 border-t border-gray-200 text-center">
          <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">Still have questions?</h3>
          <Link
            to="/support-details"
            className="inline-block px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQs;
